import React from 'react';
import { Avatar, Box, IconButton, Menu, MenuItem, SxProps, Theme, Tooltip, useMediaQuery } from '@mui/material';
import { useHistory } from 'react-router-dom';
import { useAppSelector } from 'hooks/useAppSelector';
import { RootState } from 'store';
import { useTheme } from '@mui/material/styles';
import { closeProfileMenu, openProfileMenu } from 'store/slices/menu';
import { useAppDispatch } from 'hooks/useAppDispatch';

export interface Setting {
    copy: string;
    onClick?: () => void;
    to?: string;
}

interface AvatarMenuProps {
    settings: Setting[];
    sx?: SxProps<Theme>;
}

const AvatarMenu: React.FC<AvatarMenuProps> = ({
    settings,
    sx,
}) => {
    const dispatch = useAppDispatch();
    const theme = useTheme();
    const isLg = useMediaQuery(theme.breakpoints.up('lg'));
    const boxRef = React.useRef<HTMLDivElement | null>(null);
    const menu = useAppSelector((state) => state.menu);
    const {  isProfileMenuOpen } = menu;

    const authd = useAppSelector((state: RootState) => state.auth);
    const { userLoginSuccess, jwt, tokenData } = authd;
    const { firstname, lastname, email, pictureUrl } = tokenData?.data ?? {
        firstname: undefined,
        lastname: undefined,
        email: undefined,
        picture: undefined,
    };
    const isLoggedIn = userLoginSuccess && !!jwt;
    const username = firstname || lastname
        ? `${firstname ?? ''} ${lastname ?? ''}`.trim()
        : email;

    const history = useHistory();
    const initials = username
        ? username.split(' ').map((n) => n[0].toUpperCase()).join('')
        : '';

    const handleOpenUserMenu = () => {
        dispatch(openProfileMenu());
    };

    const handleCloseUserMenu = () => {
        dispatch(closeProfileMenu());
    };

    return isLoggedIn && settings.length ? (
        <Box
            ref={boxRef}
            sx={sx}
        >
            <Tooltip title='Open settings'>
                <IconButton
                    onClick={handleOpenUserMenu}
                    sx={{
                        p: 0,
                    }}
                >
                    <Box>
                        <Avatar alt={username} src={pictureUrl ?? ''}>
                            {initials}
                        </Avatar>
                    </Box>
                </IconButton>
            </Tooltip>
            <Menu
                sx={{
                    mt: { lg: '155px', xs: '-155px' },
                }}
                id='menu-appbar'
                anchorEl={boxRef.current}
                anchorOrigin={{
                    vertical: isLg ? 'bottom' : 'top',
                    horizontal: 'right',
                }}
                keepMounted
                transformOrigin={{
                    vertical: isLg ? 'bottom' : 'top',
                    horizontal: 'right',
                }}
                open={isProfileMenuOpen}
                onClose={handleCloseUserMenu}
            >
                {settings.map((setting) => (
                    <MenuItem
                        key={setting.copy}
                        onClick={() => {
                            if (setting.onClick) {
                                setting.onClick();
                            } else if (setting.to) {
                                history.push(setting.to);
                            }
                            handleCloseUserMenu();
                        }}
                    >
                        {setting.copy}
                    </MenuItem>
                ))}
            </Menu>
        </Box>
    ) : null;
};

export default AvatarMenu;
